// Single-frame SSE field parser.
// Pure helper for createSseParser — kept separate to keep each file small.
// Spec: https://html.spec.whatwg.org/multipage/server-sent-events.html

import type { SseEvent } from './types';

// Parse one frame string (one event's worth of lines, no trailing blank line)
// into an SseEvent. Returns null if the frame contains no usable fields
// (e.g. comments only).
export function parseFrame(frame: string): SseEvent | null {
  const dataLines: string[] = [];
  let event: string | undefined;
  let id: string | undefined;
  let retry: number | undefined;
  let sawField = false;

  // Caller normalizes CRLF -> LF, so we only split on \n here.
  for (const line of frame.split('\n')) {
    if (line === '') {
      continue;
    }
    if (line.startsWith(':')) {
      continue;
    }
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? '' : line.slice(colon + 1);
    // Per spec, a single leading space after the colon is stripped.
    if (value.startsWith(' ')) {
      value = value.slice(1);
    }
    sawField = true;
    switch (field) {
      case 'data':
        dataLines.push(value);
        break;
      case 'event':
        event = value;
        break;
      case 'id':
        id = value;
        break;
      case 'retry': {
        const parsed = Number(value);
        if (Number.isFinite(parsed) && parsed >= 0) {
          retry = parsed;
        }
        break;
      }
      // Unknown fields ignored per spec.
    }
  }

  if (!sawField) {
    return null;
  }

  const result: SseEvent = { data: dataLines.join('\n') };
  if (event !== undefined) result.event = event;
  if (id !== undefined) result.id = id;
  if (retry !== undefined) result.retry = retry;
  return result;
}
